/**
 * Booking history can return one row per payment invoice for the same parcel.
 * Collapse to one row per parcel, preferring the current/latest invoice status.
 */

function isPendingStatus(statusName) {
  return String(statusName || "")
    .toLowerCase()
    .includes("pending");
}

function parcelKeyOf(row) {
  const key = row?.parcelNo ?? row?.ParcelNo ?? row?.parcelId ?? row?.id;
  return key != null && String(key).trim() !== "" ? String(key) : "";
}

/**
 * @param {Array<object>} rows
 * @returns {Array<object>} one row per parcel (stable first-seen order)
 */
export function dedupeBookingHistoryByParcel(rows = []) {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  const byParcel = new Map();
  const order = [];

  for (const row of rows) {
    const key = parcelKeyOf(row);
    if (!key) continue;

    const existing = byParcel.get(key);
    if (!existing) {
      byParcel.set(key, row);
      order.push(key);
      continue;
    }

    const existingPending = isPendingStatus(
      existing.paymentStatus || existing.statusName,
    );
    const rowPending = isPendingStatus(row.paymentStatus || row.statusName);

    // Prefer open/current (Pending due) over older Paid invoices
    if (rowPending && !existingPending) {
      byParcel.set(key, row);
      continue;
    }
    if (existingPending && !rowPending) continue;

    // Same priority — keep later row (API usually returns oldest → newest)
    byParcel.set(key, row);
  }

  return order.map((key) => byParcel.get(key)).filter(Boolean);
}

/**
 * Pick the current / latest invoice version for a parcel.
 * Prefer isCurrent; else newest non-superseded; else last row.
 * @param {Array<object>} invoices
 */
export function pickLatestInvoice(invoices = []) {
  if (!Array.isArray(invoices) || invoices.length === 0) return null;

  const current = invoices.find((r) => r.isCurrent || r.IsCurrent);
  if (current) return current;

  const nonSuperseded = invoices.filter((r) => {
    const t = String(r.adjustmentType || r.AdjustmentType || "").toLowerCase();
    return t !== "superseded";
  });
  if (nonSuperseded.length > 0) {
    return nonSuperseded[nonSuperseded.length - 1];
  }

  return invoices[invoices.length - 1];
}

export function invoiceNumberOf(row) {
  if (!row || typeof row !== "object") return "";
  const v =
    row.paymentInvoiceNo ??
    row.PaymentInvoiceNo ??
    row.invoiceNumber ??
    row.InvoiceNumber ??
    "";
  return v != null && String(v).trim() !== "" ? String(v).trim() : "";
}

function paymentIdOf(row) {
  if (!row || typeof row !== "object") return null;
  const id = row.paymentId ?? row.PaymentId ?? row.id ?? row.Id;
  if (id == null || id === "") return null;
  const n = Number(id);
  return Number.isFinite(n) ? n : null;
}

/** Due / Credit / Amended versions — not original bank-transfer notes. */
export function isRealInvoiceAmendment(adjustmentType) {
  const t = String(adjustmentType || "").trim().toLowerCase();
  return t === "amended" || t === "due" || t === "credit";
}

export function pickAmendmentReason(source) {
  if (!source || typeof source !== "object") return "";
  const raw =
    source.description ??
    source.Description ??
    source.reason ??
    source.Reason ??
    source.amendmentReason ??
    source.AmendmentReason ??
    source.amendmentNote ??
    source.note;
  const s = raw != null ? String(raw).trim() : "";
  if (!s) return "";
  const lower = s.toLowerCase();
  if (lower.startsWith("manual bank transfer")) return "";
  return s;
}

/**
 * Amendment events for a parcel: when (invoice creation) and why (reason/note).
 * @param {{ invoices?: object[], printByInvoiceNo?: Record<string, object>, paymentHistory?: object|null }} args
 */
export function buildParcelAmendments({
  invoices = [],
  printByInvoiceNo = {},
  paymentHistory = null,
} = {}) {
  const list = Array.isArray(invoices) ? invoices : [];
  const historyItems = Array.isArray(paymentHistory?.items)
    ? paymentHistory.items
    : [];

  const parentInvoiceNo = (row) => {
    const parentId = row?.parentPaymentId ?? row?.ParentPaymentId;
    if (parentId == null || parentId === "") return "";
    const parent = list.find((r) => paymentIdOf(r) === Number(parentId));
    return invoiceNumberOf(parent);
  };

  const historyByInvoice = new Map();
  const historyByPaymentId = new Map();
  historyItems.forEach((item) => {
    const no = invoiceNumberOf(item);
    if (no) historyByInvoice.set(no, item);
    const pid = paymentIdOf(item);
    if (pid != null) historyByPaymentId.set(pid, item);
  });

  const fromInvoices = list
    .filter((inv) =>
      isRealInvoiceAmendment(inv.adjustmentType ?? inv.AdjustmentType),
    )
    .map((inv) => {
      const invoiceNo = invoiceNumberOf(inv);
      const print = (invoiceNo && printByInvoiceNo[invoiceNo]) || {};
      const hist =
        historyByInvoice.get(invoiceNo) ||
        historyByPaymentId.get(paymentIdOf(inv)) ||
        {};
      return {
        adjustmentType: inv.adjustmentType || inv.AdjustmentType,
        version: inv.version ?? inv.Version ?? null,
        paymentInvoiceNo: invoiceNo,
        parentInvoiceNo: parentInvoiceNo(inv),
        when:
          inv.creationDate ||
          inv.CreationDate ||
          print.creationDate ||
          hist.creationDate ||
          "",
        why:
          pickAmendmentReason(print) ||
          pickAmendmentReason(inv) ||
          pickAmendmentReason(hist),
        orderAmount: inv.orderAmount ?? print.orderAmount ?? hist.orderAmount,
        isCurrent: Boolean(inv.isCurrent || inv.IsCurrent),
      };
    });

  const rows =
    fromInvoices.length > 0
      ? fromInvoices
      : historyItems
          .filter((item) => isRealInvoiceAmendment(item.adjustmentType))
          .map((item) => ({
            adjustmentType: item.adjustmentType,
            version: item.version ?? null,
            paymentInvoiceNo: invoiceNumberOf(item),
            parentInvoiceNo: "",
            when: item.creationDate || "",
            why: pickAmendmentReason(item),
            orderAmount: item.orderAmount,
            isCurrent: false,
          }));

  return rows.sort((a, b) => {
    const ta = a.when ? new Date(a.when).getTime() : 0;
    const tb = b.when ? new Date(b.when).getTime() : 0;
    return ta - tb;
  });
}
